import { Component, ViewChild, OnInit, OnDestroy, inject } from "@angular/core";
import { NgForm } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Command, LabelCommand } from "@tylertech/commands-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { CachedRepositoryService, KeyHandlerService } from "@tylertech/core-module";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";
import { Utilities } from "@tylertech/shared-module";
import { ICombinedViewMetadata, IMetadataAction, IViewMetadata, ModifierKey, RepositoryDataCollection, } from "@tylertech/tyler-api-types";
import { RepositoryFormData } from "../../interfaces";
import { GridValidationService, MetadataActionService, MetadataErrorService, MetadataEventService, MetadataPropertyService, MetadataService, MetadataViewService } from "../../services";
import { MetadataActionHelpers } from "../../utilities";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

type BeforeCompleteFn = (action: IMetadataAction, model: any, formData: RepositoryDataCollection) => Promise<any>;

export interface MetadataActionDialogData
{
    action: IMetadataAction;
    model: any;
    datacontroller: RepositoryDataCollection;
    /** @deprecated - this functionality should be moved into the ActionDialogComponent, it doesn't need to be structured like this... */
    beforeComplete: BeforeCompleteFn;
    dialogIndex: number;
}

function tryExecute(cmd: Command): void
{
    if (cmd.isVisible && cmd.isEnabled)
    {
        cmd.action();
    }
}

/**
 * @private ONLY INTENDED TO BE USED FROM THE $metadataActionService!
 */
@Component({
    selector: "metadata-action-dialog",
    templateUrl: "./metadataActionDialog.tmpl.html",
    standalone: false
})
export class MetadataActionDialogComponent implements OnInit, OnDestroy
{
    private readonly dialogRef = inject<MatDialogRef<MetadataActionDialogComponent>>(MatDialogRef);
    private readonly dialogData = inject<MetadataActionDialogData>(MAT_DIALOG_DATA);
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $gridValidationService = inject(GridValidationService);
    private readonly $keyHandlerService = inject(KeyHandlerService);
    private readonly $metadataActionService = inject(MetadataActionService);
    private readonly $metadataErrorService = inject(MetadataErrorService);
    private readonly $metadataEventService = inject(MetadataEventService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    private readonly $metadataService = inject(MetadataService);
    private readonly $metadataViewService = inject(MetadataViewService);
    private readonly $repositoryService = inject(CachedRepositoryService);

    private readonly log = getLoggerFor("MetadataActionDialogComponent");
    @ViewChild("form") readonly form: NgForm;

    readonly commands: ReadonlyArray<Command>;
    viewRecalculating: boolean = false;
    processing: boolean;
    isTcm: boolean;
    cantSave: boolean;
    saveAfterRecalculate: boolean = false;
    saveAttempted: boolean = false;
    listenerId: string;

    readonly action: IMetadataAction;
    readonly model: any;
    readonly datacontroller: RepositoryFormData;
    readonly beforeComplete: BeforeCompleteFn;
    readonly dialogIndex: number;

    readonly onDelete = this.onDeleteImpl.bind(this);

    drawWidth: string = "80vw";

    constructor()
    {
        this.dialogRef.disableClose = true;

        this.listenerId = Utilities.generateRandomGuid();

        this.action = this.dialogData.action;
        this.model = this.dialogData.model;
        this.datacontroller = RepositoryFormData.create(this.dialogData.datacontroller);
        this.beforeComplete = this.dialogData.beforeComplete;
        this.dialogIndex = this.dialogData.dialogIndex;

        this.isTcm = this.action.ViewToShow.IsTCMAttachmentView === true;
        this.cantSave = this.action.ViewToShow.CantSave === true;

        const cancelCommand = new LabelCommand({
            label: this.getCancelLabel(),
            action: () => this.cancel(),
            getEnabled: () => true,
            getVisible: () => this.showCancel,
        });

        const saveCommand = new LabelCommand({
            type: "raised",
            label: this.getSaveLabel(),
            action: () => this.maybeWaitForRecalculate(),
            getEnabled: () => this.canSave,
            getVisible: () => !this.isTcm && !this.action.ViewOnly,
            getWaiting: () => (!this.view.OnInterval && this.viewRecalculating) || this.processing
        });

        this.commands = [
            cancelCommand,
            saveCommand,
        ];

        const cancelShortcutKey = Utilities.getShortcutKeyForLabel(cancelCommand.label);
        if (cancelShortcutKey)
        {
            this.$keyHandlerService.subscribe(
                this.listenerId,
                cancelShortcutKey,
                ModifierKey.Alt,
                `Dialog|${cancelCommand.label}`,
                () =>
                {
                    tryExecute(cancelCommand);
                }
            );
        }

        const saveShortcutKey = Utilities.getShortcutKeyForLabel(this.getSaveLabel());
        if (saveShortcutKey)
        {
            this.$keyHandlerService.subscribe(
                this.listenerId,
                saveShortcutKey,
                ModifierKey.Alt,
                "Dialog|Save form data",
                () =>
                {
                    tryExecute(saveCommand);
                }
            );
        }

        if (this.action.ViewToShowProperty)
        {
            this.$metadataEventService.onMetadataRefresh.pipe(takeUntilDestroyed())
                .subscribe(() =>
                {
                    const newView = this.$metadataService.DependentModuleValue(
                        this.action.ViewToShowProperty,
                        this.model["$$Parent"],
                        this.model);

                    if (!!newView && this.$metadataViewService.IsOneToOneView(newView))
                    {
                        this.$metadataViewService.UpdateViewMetadata([this.action.ViewToShow], [newView]);
                        delete this.model[this.action.ViewToShowProperty];
                        this.$metadataService.FireGridReload(null, true, null, true);
                    }
                });
        }

        this.$metadataEventService.onSaveAfterRecalculate.pipe(takeUntilDestroyed())
            .subscribe(() =>
            {
                if ((this.saveAttempted && this.saveAfterRecalculate) || this.$metadataActionService.closeDialogAction)
                {
                    this.$metadataActionService.closeDialogAction = false;
                    this.saveAfterRecalculate = false;
                    this.save();
                }
            });
    }

    get showBusyIndicator(): boolean
    {
        return this.processing && !this.view["IsExternalUrlMetadataForm"];
    }

    get canSave(): boolean
    {
        if (this.processing
            || this.$metadataViewService.IsViewCellEditing(this.view)
            || (!this.view.OnInterval && this.viewRecalculating))
        {
            return false;
        }

        return true;
    }

    get showCancel(): boolean
    {
        return !this.cantSave
            && (MetadataActionHelpers.hasCustomServicePath(this.action)
                || this.action.ViewToShow.IsRepositoryType
                || MetadataActionHelpers.isRepositorySaveAll(this.action)
                || MetadataActionHelpers.isRequiredValid(this.action)
                || this.isTcm
                || this.action.ViewOnly);
    }

    get title(): string
    {
        return this.action.ViewToShow.Title || this.action.Label;
    }

    get view(): IViewMetadata
    {
        return this.action.ViewToShow;
    }

    get isBackgroundDialog(): boolean
    {
        return this.action.ViewToShow.ViewMetaData?.TypeName === "Api.Core.EF.BackgroundTasks.BackgroundTaskViewModel";
    }

    ngOnInit(): void
    {
        const width = Math.min(80, this.action.ViewToShow.DrawWidth ?? 80);
        this.dialogRef.updateSize(`${width}vw`);
    }

    ngOnDestroy(): void
    {
        this.$keyHandlerService.unsubscribeAllFromClass(this.listenerId);
    }

    private getCancelLabel(): string
    {
        // TCM saves are not handled by us, so don't process, validate, or save - just close.
        if (this.isTcm || this.action.ViewOnly || this.isBackgroundDialog)
        {
            return "Close";
        }
        return "Cancel";
    }

    private getSaveLabel(): string
    {
        if (this.action.SaveActionLabel)
        {
            return this.action.SaveActionLabel;
        }

        return !MetadataActionHelpers.hasCustomServicePath(this.action) && !this.action.ViewToShow.IsRepositoryType
            ? "OK"
            : this.cantSave
                ? "Done"
                : "Save";
    }

    @LogInvocation(LogLevel.DEBUG)
    private maybeWaitForRecalculate(): void
    {
        if (this.form.invalid)
        {
            this.$metadataService.touchForm(this.form.controls);
        }
        this.$gridValidationService.triggerValidateCellGrid();

        const errors = this.$metadataErrorService.getViewMetadataErrors(this.view);
        const cellEditErrors = this.$metadataErrorService.getCellEditErrors(this.view, this.datacontroller, this.model, this.action);

        if ((this.form.invalid && errors && errors.length > 0)
            || (cellEditErrors.length > 0 && !MetadataActionHelpers.isRequiredValid(this.action)))
        {
            this.$metadataErrorService.showVerificationAlertDialog(errors.concat(cellEditErrors));
            return;
        }

        requestAnimationFrame(() =>
        {
            if (this.viewRecalculating)
            {
                this.saveAfterRecalculate = true;
            }
            else
            {
                this.save();
            }

            this.saveAttempted = true;
        });
    }

    @LogInvocation(LogLevel.DEBUG)
    private save($event?: MouseEvent): void
    {
        $event?.stopPropagation();

        this.saveAttempted = false;
        this.processing = true;
        this.beforeComplete(this.action, this.model, this.datacontroller)
            .then(
                validatedModel => this.close(validatedModel),
                err =>
                {
                    this.processing = false;
                    if (err?.error?.VerificationId)
                    {
                        const callback = (): Promise<void> => Promise.resolve(this.save());
                        const handler = this.$metadataActionService.GetVerificationHandler(
                            this.datacontroller,
                            callback,
                            this.model
                        );
                        return handler(err);
                    }

                    if (err.error || err.Verifications)
                    {
                    //This line prevents undefined error messages that were sent in error.
                        const errorList = this.$metadataErrorService.getErrorList(err);
                        const msg = errorList && errorList.length > 0
                            ? errorList.reduce((p, x) => `${p}<li>${x}</li>`, "")
                            : "Create failed.";
                        this.$commonDialogsService.showAlertMessage(msg);
                    }
                }
            );
    }

    private isImmediateDelete(): boolean
    {
        if ("Views" in this.view)
        {
            const combinedView = <ICombinedViewMetadata>this.view;
            for (let index = 0; index < combinedView.Views.length; index++)
            {
                const currentView = combinedView.Views[index];
                if (!Utilities.IsNullOrUndefined(currentView.ImmediateDelete) && currentView.ImmediateDelete === true)
                    return true;
            }
        }
        const immediateDelete =
            !Utilities.IsNullOrUndefined(this.view.ImmediateDelete) && this.view.ImmediateDelete === true;
        return immediateDelete;
    }

    private onDeleteImpl({typename, model}: {typename: string, model: any}): Promise<any>
    {
        const cantSave = !Utilities.IsNullOrUndefined(this.view.CantSave) && this.view.CantSave === true;
        const immediateDelete = this.isImmediateDelete();
        if (!immediateDelete && !cantSave)
        {
            return Promise.resolve();
        }

        return this.$repositoryService.Save(
            {
                Item1: typename,
                Item2: Utilities.stringify(model),
            })
            .then(
                _r =>
                {
                    if (!this.isImmediateDelete())
                    {
                        this.$repositoryService.ClearAll();
                    }
                },
                e =>
                {
                    model.SerializedRowState = "Unchanged";
                    return this.$commonDialogsService.showAlertHtmlMessage(e.error.Message, "Acknowledge");
                });
    }

    @LogInvocation(LogLevel.DEBUG)
    private close(model: any): void
    {
        this.processing = false;
        if (this.action["$fromVQH"])
        {
            this.$metadataPropertyService.recalculateVQHActionInterrupt = false;
        }
        this.dialogRef.close(model);
    }

    @LogInvocation(LogLevel.DEBUG)
    private cancel(): void
    {
        if (this.action["$fromVQH"])
        {
            this.$metadataPropertyService.recalculateVQHActionInterrupt = false;
        }

        if (this.isTcm)
        {
            if (this.isTcm && !Utilities.IsNullOrUndefined(this.action.LookupForLabel))
            {
                this.$metadataEventService.fireLookupTypeCacheClearListener(this.action.LookupForLabel);
            }
            this.dialogRef.close(null);
        }
        else
        {
            this.dialogRef.close({ canceledActionDialog: true });
        }
    }
}
